import { FormState } from '../../models';
import { MonitorSeriesNames } from '../../../location-card.constants';
import { tritonFormState } from '../triton/base';
import { echoFormState } from '../echo/base';
import { rainFormState } from '../rain-alert/base';
import { foresiteULFormState } from '../foresite-ul/base';
import { foresiteXDFormState } from '../foresite-xd/base';
import { unknownFormState } from '../unknown/base';
import { flexFormState } from '../flex/base';
import { LocationCardState } from '../../reducers';
import { basicInfo } from './basic-info';
import { dialogActions } from './dialog-actions';
import { genericFormState } from '../generic/base';
import { edgeRlFormState } from '../edge-rl/base';
import { edgeDxFormState } from '../edge-dx/base';
import { configureAlarms } from './alarms';
import { dataDelivery } from './data-delivery';
import { AlarmDevice, isIAlarmsExtendedLowecase, isIAlarmsLowecase, translateAlarmDeviceToAlarmsExtended, translateAlarmDeviceToAlarmsStandard, translateAlarmsExtendedToAlarmDevice, translateAlarmsStandardToAlarmDevice } from '../../models/alarms'
import { EchoLocationCardState } from '../echo/models';
import { FlexLocationCardState } from '../flex/models';
import { isTritonConfig } from '../base-utils';
import { NonSensorDeviceNames } from 'app/shared/enums/device-type-code';
import { ForesiteULLocationCardState } from '../foresite-ul/models';
import { ForesiteXDLocationCardState } from '../foresite-xd/models';
import { GenericLocationCardState } from '../generic/models';
import { EdgeRlLocationCardState } from '../edge-rl/models';
import { InitialLocationCardState } from './model';
import { AlarmDevice as AlarmDeviceConfig, TritonLocationCardState } from '../triton/models';
import { RainAlertLocationCardState } from '../rain-alert/models';
import { UnknownLocationCardState } from '../unknown/models';

/**
 * Main configuration
 */
export const initialFormConfig = (state: InitialLocationCardState): FormState => {
    return {
        config: {
            info: {
                id: 'location-card-config',
                title: 'Location Card',
            },
            sections: [
                basicInfo(state),
                configureAlarms(state),
                dataDelivery(state),
                dialogActions(state),
            ],
        },
        pristine: true,
        valid: false,
        submitted: false,
    };
};

export const getGlobalConfigBySeries = (series: string, state: LocationCardState<unknown>): FormState => {
    /**
     * We need to checkout and translate original values (for example TRITON to currently selected series type values)
     */
    switch (series.toLowerCase()) {
        case MonitorSeriesNames.triton.toLowerCase(): {
            const tritonState = state as TritonLocationCardState;
            const monitorConfiguration = hasDeviceArray(tritonState.monitorConfiguration)
                ? tritonState.monitorConfiguration
                : { ...tritonState.monitorConfiguration, devices: buildAlarmDevices(state.monitorConfiguration) };

            return tritonFormState({ ...tritonState, monitorConfiguration });
        }
        case MonitorSeriesNames.echo.toLowerCase():
            let echoState = state as EchoLocationCardState;
            echoState = convertFromAlarmDevice(echoState, state, translateAlarmDeviceToAlarmsStandard);

            return echoFormState(echoState);
        case MonitorSeriesNames.rainAlert.toLowerCase():
            const rainState = state as RainAlertLocationCardState;

            return rainFormState(rainState);
        case MonitorSeriesNames.ForeSITE_UL.toLowerCase():
            let foresiteULState = state as ForesiteULLocationCardState;
            foresiteULState = convertFromAlarmDevice(foresiteULState, state, translateAlarmDeviceToAlarmsStandard);

            return foresiteULFormState(foresiteULState);
        case MonitorSeriesNames.ForeSITE_XD.toLowerCase():
            let foresiteXDState = state as ForesiteXDLocationCardState;
            foresiteXDState = convertFromAlarmDevice(foresiteXDState, state, translateAlarmDeviceToAlarmsStandard);

            return foresiteXDFormState(foresiteXDState);
        case MonitorSeriesNames.flex.toLowerCase(): {
            const flexState = state as FlexLocationCardState;
            const monitorConfiguration = hasDeviceArray(flexState.monitorConfiguration)
                ? flexState.monitorConfiguration
                : { ...flexState.monitorConfiguration, devices: buildAlarmDevices(state.monitorConfiguration) };

            return flexFormState({ ...flexState, monitorConfiguration });
        }
        case MonitorSeriesNames.genericMonitor.toLowerCase():
            return genericFormState(state as GenericLocationCardState);
        case MonitorSeriesNames.edgeDx.toLowerCase():
            return edgeDxFormState(state);
        case MonitorSeriesNames.edgeRl.toLowerCase():
            return edgeRlFormState(state as EdgeRlLocationCardState);

        default:
            return unknownFormState(state as UnknownLocationCardState, series);
    }
};

function hasDeviceArray(config: unknown): boolean {
    return !!config && typeof config === 'object' && 'devices' in config && Array.isArray(config.devices);
}

function buildAlarmDevice(alarmValues: Partial<AlarmDevice>): AlarmDeviceConfig {
    return {
        name: NonSensorDeviceNames.Alarm1,
        type: '',
        installationNumber: 0,
        active: true,
        rainEnable: false,
        rainThreshold: 0,
        rainReturnToNormal: 0,
        batteryLowEnable: false,
        batteryLowThreshold: 0,
        batteryDeadEnable: false,
        temperatureInternalEnable: false,
        temperatureExternalEnable: false,
        temperaturePseudoEnable: false,
        lowLevelEnable: false,
        lowLevelThreshold: 0,
        highLevelEnable: false,
        highLevelThreshold: 0,
        highHighEnable: false,
        highHighThreshold: 0,
        fullPipeEnable: false,
        fullPipeThreshold: 0,
        overflowEnable: false,
        overflowThreshold: 0,
        tiltEnable: false,
        tiltThreshold: 0,
        unidepthsToAverage: 0,
        highFlowEnable: false,
        highFlowThreshold: 0,
        ...alarmValues,
    };
}

function buildAlarmDevices(sourceConfig: unknown): AlarmDeviceConfig[] {
    if (isIAlarmsExtendedLowecase(sourceConfig)) {
        return [buildAlarmDevice(translateAlarmsExtendedToAlarmDevice(sourceConfig))];
    }

    if (isIAlarmsLowecase(sourceConfig)) {
        return [buildAlarmDevice(translateAlarmsStandardToAlarmDevice(sourceConfig))];
    }

    return [];
}

function convertFromAlarmDevice<T, U>(modifiedState: LocationCardState<U> , originalState: LocationCardState<unknown>, convertFn: (alarmDevice: AlarmDevice) => T ): LocationCardState<U> {
    if(isTritonConfig(originalState.monitorConfiguration) && originalState.monitorConfiguration.devices) {
        const alarmData = originalState.monitorConfiguration.devices.find((v) => v.name === NonSensorDeviceNames.Alarm1) as AlarmDevice | undefined;
        if (alarmData) {
            modifiedState = {...modifiedState, ...convertFn(alarmData)};
        }
    }

    return modifiedState;
}
